import { logError, isEmpty } from 'src/utils/helpers'

/**
 * Default properties that should be received with `/api/kiosk/brand/getKioskBrandSettings` endpoint for each feature.
 *
 * @type {object}
 */
// const defaultFeatureProperties = {
//   // Required main label
//   name: '',
//   // Smaller text below main label
//   description: null,
//   // If it's one of our features:
//   // ["requestQuote"|"documents"|"reportClaim"|"payments"|"inventory"|"services"]
//   // or some unique string if it's for example custom page content
//   feature: null,
//   // Internal URL
//   url: null,
//   // If this feature just opens external URL
//   externalUrl: null,
//   isExternalUrl: false,
//   // For setting left icon in main menu list
//   iconUrl: null,
//   // Backend (`campayn/campayn`) API endpoint that will return custom content when page of this feature is opened,
//   // without base URL (e.g. https://pathwayport.com/api/kiosk)
//   requestPath: null,
//   // Feature type:
//   // ["default"|"customPage"|"form"]
//   type: 'default',
//   // Sort/order, higher the number - lower it is on the list,
//   // but if not set, feature will be shown in order in which they came in from backend
//   order: 0,
// }

/**
 * Some properties will be overwritten form backend ("url" will stay and it should match internal Kiosk routes)
 *
 * // TODO: PAT-3298 Deprecate, because backend should dictate which features show up on kiosk
 *
 * @type {array}
 */
const defaultFeatureSettings = [
  {
    leftIcon: ['fal', 'calculator'],
    name: 'Get Quote',
    id: 'requestQuote',
    feature: 'requestQuote',
    description: 'Get an instance quote now',
    url: '/quote',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: ['fal', 'files'],
    name: 'My Documents',
    id: 'documents',
    feature: 'documents',
    description: 'Access your insurance documents',
    url: '/documents',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: ['fal', 'file-pen'],
    name: 'Change My Policy',
    id: 'changePolicy',
    feature: 'changePolicy',
    description: 'Update home, vehicle and more',
    url: '/changepolicy',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: ['fal', 'clipboard-list'],
    name: 'Report Claim',
    id: 'reportClaim',
    feature: 'reportClaim',
    description: 'Submit an auto claim',
    url: '/claims',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: ['fal', 'wallet'],
    name: 'Payments',
    id: 'payments',
    feature: 'payments',
    description: 'Send a payment to your broker',
    url: '/payments',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: ['fal', 'box-heart'],
    name: 'Home Inventory',
    id: 'inventory',
    feature: 'inventory',
    description: 'Organize the value of the personal belongings',
    url: '/inventory',
    isExternalUrl: false,
    rightIcon: 'right',
  },
  {
    leftIcon: ['fal', 'car-wrench'],
    name: 'Road Services',
    id: 'services',
    feature: 'services',
    description: 'Gas, Locksmiths, Tow Truck, Police',
    url: '/services',
    isExternalUrl: false,
    rightIcon: 'right',
  },
]

/**
 * Extract feature URL
 *
 * @param features Combined features
 * @param feature
 * @return {string|null|*}
 */
export function getInternalFeatureUrl(features, feature) {
  try {
    // Handle Chat since it's not on this list
    if (feature.toLowerCase() === 'chat') {
      return '/chat'
    }

    const findFeature = features.find(f => f.id.toLowerCase() === feature.toLowerCase())

    if (findFeature && findFeature.hasOwnProperty('page') && findFeature.page.externalUrl) {
      return null
    }

    if (findFeature && findFeature.hasOwnProperty('url')) {
      return findFeature.url
    }

    return null
  } catch (e) {
    return null
  }
}

/**
 * Check if feature is selected in Pathway settings and it doesn't have custom URL
 *
 * @param {string} feature Feature to check
 * @param {object} features List of enabled or disable feature, just true/false without details
 * @return {boolean}
 */
export function isFeatureUsed(feature, features) {
  let foundedFeature
  try {
    // If feature is definitely not enabled
    if (!(feature && features.length)) {
      return false
    } else {
      foundedFeature = features.find(f => f.id === feature)
      if (foundedFeature && !foundedFeature.isEnabled) {
        return false
      }
    }

    // If feature has custom URL - so don't use it for homepage
    return foundedFeature
  } catch (e) {
    return false
  }
}

/**
 * Checks if Chat is only enabled feature.
 * Used for hiding bottom navigation, redirecting to /chat from home page, etc.
 *
 * @param {object} settings{} All settings received from backend
 * @param {object} settings{}.features Features selected and saved on Pathway side
 * @return {boolean}
 */
export function checkIfChatOnly(data) {
  try {
    // Exit if chat is not even enabled
    if (!(data && data.hasOwnProperty('chat') && data.chat.isEnabled)) {
      return false
    }
    const enabledFeatures = data.features.map(feature => {
      return feature.isEnabled
    })

    return !enabledFeatures.includes(true)
  } catch (e) {
    logError('Check if chat only', e)
    return false
  }
}

/**
 * Check if chat is currently enabled, based on additional chat options (hidden after working hours, etc.),
 * and if it's even enabled of course.
 *
 * @param chat
 * @param chatOptions
 * @param isAfterHours
 * @param isChatEmpty
 * @return {boolean}
 */
export function isChatCurrentlyEnabled(chat, chatOptions, isAfterHours, isChatEmpty) {
  const defaultResponse = false
  try {
    // Chat not turned on
    if (!chat.isEnabled) {
      return defaultResponse
    }

    const isHideChatAfterHours =
      !isEmpty(chatOptions) &&
      chatOptions.hasOwnProperty('hideChatAfterHours') &&
      chatOptions.hideChatAfterHours === true

    if (isChatEmpty && isAfterHours && isHideChatAfterHours) {
      return defaultResponse
    }

    return true
  } catch (e) {
    logError('Check if chat is currently enabled', e)
    return defaultResponse
  }
}

/**
 * Bottom navigation is only needed if chat is enabled and if it's not the only feature.
 *
 * @param features List of all features with true/false values
 * @param chatOptions Additional Chat options (e.g. hideChatAfterHours)
 * @param isAfterHours Calculated based on working hours
 * @param isChatEmpty If there are no messages in the chat
 * @param isChatOnlyFeature
 * @return {boolean}
 */
export function isNavBottomVisible(isChat) {
  const defaultResponse = false

  try {
    if (isChat) {
      return defaultResponse
    }

    return true
  } catch (e) {
    logError('Check if bottom navigation is needed', e)
    return defaultResponse
  }
}

/**
 * Takes default/fallback features and combines them with features received from PW API
 *
 * // TODO: PAT-3298 Remove if it works properly with new code (getFeaturesLegacy())
 *
 * @deprecated Because this was able to show only features defined here on Kiosk side,
 * but now we're taking features from backend directly (using getFeaturesAndOptions())
 */
/* export default (selectedFeatures, allLabels, claimOptions = {}) => {
  const features = selectedFeatures || {}
  const labels = allLabels && allLabels.hasOwnProperty('features') ? allLabels.features : {}
  const showClaimNumbers = claimOptions.hasOwnProperty('showClaimNumbers') && claimOptions.showClaimNumbers === true

  return defaultFeatureSettings.reduce((acc, item) => {
    let newItem = {
      ...item
    }

    if (newItem.hasOwnProperty('feature') && features.hasOwnProperty(newItem.feature) && features[newItem.feature]) {

      if(newItem.feature === 'reportClaim' && showClaimNumbers === true) {
        newItem.url = '/claims'
      }

      if(labels.hasOwnProperty(newItem.feature)) {
        const labelItem = labels[newItem.feature]

        if(labelItem.hasOwnProperty('label')) {
          newItem.name = labelItem.label
        }
        if(labelItem.hasOwnProperty('sublabel')) {
          newItem.description = labelItem.sublabel
        }

        if (!isEmpty(labelItem.url)) {
          newItem.url = labelItem.url
          newItem.isExternalUrl = true
        }
      }

      acc.push(newItem)
    }

    return acc
  }, [])
}*/

/**
 * Clean and sort features.
 *
 * @param {array} features
 * @return {array}
 */
// const sortAvailableFeatures = (features) => {
//   try {
//     return (
//       features
//         // Filter out empty features
//         .filter((feature) => feature !== null)
//         .sort((a, b) => {
//           if (!(a.hasOwnProperty('order') && b.hasOwnProperty('order'))) {
//             return 0
//           }
//           // ASC sort
//           return a.order - b.order
//         })
//     )
//   } catch (e) {
//     return features
//   }
// }

/**
 * Format features received from PW API
 *
 * // TODO: PAT-3298 Deprecate later when it's cleared from all device's cache/pre-cache,
 *   because backend should dictate which features show up on kiosk...
 */
export function getFeaturesLegacy(features, labels, claimOptions = {}) {
  const defaultResponse = []

  try {
    // Exit if we don't have any feature to process (e.g. we didn't receive any from PW API)
    if (isEmpty(features)) {
      return defaultResponse
    }

    // Take additional feature settings (currently from "labels") from backend
    const featuresSettings = labels && labels.hasOwnProperty('features') ? labels.features : {}
    const showClaimNumbers =
      claimOptions.hasOwnProperty('showClaimNumbers') && claimOptions.showClaimNumbers === true

    const combinedFeatures = Object.keys(features).map(featureKey => {
      // Exit if Chat
      if (featureKey === 'chat') {
        return null
      }

      // Exit if feature is not enabled
      if (!(features.hasOwnProperty(featureKey) && features[featureKey] === true)) {
        return null
      }

      // Exit if we didn't get additional settings from backend
      if (
        !(featuresSettings.hasOwnProperty(featureKey) && !isEmpty(featuresSettings[featureKey]))
      ) {
        return null
      }

      // Take additional settings from backend
      const featureSettings = featuresSettings[featureKey]

      const defaultSettings = defaultFeatureSettings.find(f => f.feature === featureKey)
      let newItem = !isEmpty(defaultSettings) ? defaultSettings : {}

      if (
        newItem.hasOwnProperty('feature') &&
        newItem.feature === 'reportClaim' &&
        showClaimNumbers === true
      ) {
        newItem.url = '/claims'
      }

      if (featureSettings.hasOwnProperty('label')) {
        newItem.name = featureSettings.label
      }
      if (featureSettings.hasOwnProperty('sublabel')) {
        newItem.description = featureSettings.sublabel
      }

      if (!isEmpty(featureSettings.url)) {
        newItem.url = featureSettings.url
        newItem.isExternalUrl = true
      }

      if (isEmpty(newItem)) {
        return null
      }

      return newItem
    })

    return combinedFeatures
  } catch (e) {
    logError('Get features', e)
    return defaultResponse
  }
}

/**
 * Format features and options received from backend (`/api/kiosk/brand/features`).
 *
 * Also adds Kiosk icons and URLs, since backend doesn't know those.
 *
 * @param {object} features Received from backend
 * @return {[]}
 */
export function getFeaturesAndOptions(features) {
  const defaultResponse = []
  try {
    const combinedFeatures = features.map(feature => {
      const featureKey = feature.hasOwnProperty('id') && feature.id ? feature.id : ''
      // Exit if name (main label) is not defined
      if (!(feature.hasOwnProperty('title') && feature.title.length > 0)) {
        return null
      }

      // Merge default and custom properties from backend
      let newItem = {
        ...feature,
      }

      // Set custom icons and URLs for known features (backend doesn't know these)
      if (featureKey === 'requestQuote') {
        if (feature.icon.type === 'default') {
          newItem.leftIcon = ['fal', 'calculator']
        } else {
          newItem.leftIcon = feature.icon.customImage
            ? feature.icon.customImage
            : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        }
        newItem.url = '/quote'
      }
      if (featureKey === 'documents') {
        if (feature.icon.type === 'default') {
          newItem.leftIcon = ['fal', 'files']
        } else {
          newItem.leftIcon = feature.icon.customImage
            ? feature.icon.customImage
            : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        }
        newItem.url = '/documents'
      }
      if (featureKey === 'changePolicy') {
        if (feature.icon.type === 'default') {
          newItem.leftIcon = ['fal', 'file-pen']
        } else {
          newItem.leftIcon = feature.icon.customImage
            ? feature.icon.customImage
            : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        }
        newItem.url = '/changepolicy'
      }
      if (featureKey === 'reportClaim') {
        if (feature.icon.type === 'default') {
          newItem.leftIcon = ['fal', 'clipboard-list']
        } else {
          newItem.leftIcon = feature.icon.customImage
            ? feature.icon.customImage
            : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        }
        newItem.url = '/claims'
      }
      if (featureKey === 'payments') {
        if (feature.icon.type === 'default') {
          newItem.leftIcon = ['fal', 'wallet']
        } else {
          newItem.leftIcon = feature.icon.customImage
            ? feature.icon.customImage
            : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        }
        newItem.url = '/payments'
      }
      if (featureKey === 'inventory') {
        if (feature.icon.type === 'default') {
          newItem.leftIcon = ['fal', 'box-heart']
        } else {
          newItem.leftIcon = feature.icon.customImage
            ? feature.icon.customImage
            : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        }
        newItem.url = '/inventory'
      }
      if (featureKey === 'services') {
        if (feature.icon.type === 'default') {
          newItem.leftIcon = ['fal', 'car-wrench']
        } else {
          newItem.leftIcon = feature.icon.customImage
            ? feature.icon.customImage
            : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        }
        newItem.url = '/services'
      }

      if (feature.page.type === 'externalUrl' && feature.page.externalUrl) {
        newItem.url = feature.page.externalUrl
        newItem.isExternalUrl = true
      }

      if (feature.id === 'customPage') {
        newItem.leftIcon = feature.icon.customImage
          ? feature.icon.customImage
          : [feature.icon.fontAwesome.prefix, feature.icon.fontAwesome.iconName]
        newItem.url =
          feature.page.type && feature.page.type !== 'customHtml'
            ? feature.page.externalUrl
            : (newItem.url = '/cp')
      }

      return newItem
    })
    return combinedFeatures
  } catch (e) {
    return defaultResponse
  }
}
